import store from '../store';
import { ACTIONS } from './actions';
import * as API from '../../api/client';
import { getSelectedFlow, getTenantId } from '../selectors';
export function apiRequest(apiFunc, type, params) {
  return async function (dispatch) {
    dispatch({ type, status: 'request', params });

    return apiFunc()
      .then((response) => dispatch({ type, status: 'success', payload: response, params }))
      .catch((err) => dispatch({ type, status: 'error', payload: err, params }));
  };
}

// General
export function fetchPreferences() {
  return apiRequest(() => API.getPreferences(), ACTIONS.FETCH_PREFERENCES);
}

export function fetchFeatureFlags() {
  return apiRequest(() => API.getFeatureFlags(), ACTIONS.FETCH_FEATURE_FLAGS);
}

// Sources
export function fetchLinkedSources(tenant = getTenantId(), flowId = getSelectedFlow()) {
  const preferences = store.getState().preferences;
  return apiRequest(
    () => API.getLinkedSources(undefined, undefined, flowId),
    ACTIONS.FETCH_LINKED_SOURCES,
    {
      tenant,
      multipleTenantLinks: preferences.multipleTenantLinks,
      subtenantDelimiter: preferences.featureFlags?.subtenantDelimiter ?? '_',
    }
  );
}

export function fetchLinkedSourceByFlowAndTenant(tapName, flow, tenant) {
  const preferences = store.getState().preferences;
  return apiRequest(
    () => API.getLinkedSources(tapName, false, flow, tenant),
    ACTIONS.FETCH_LINKED_SOURCES,
    {
      tenant,
      multipleTenantLinks: preferences.multipleTenantLinks,
      subtenantDelimiter: preferences.featureFlags?.subtenantDelimiter ?? '_',
    });
}

export function fetchLinkedConnectorByFlowAndTenant(id, flow, tenant) {
  return apiRequest(() => API.getLinkedConnectors(id, false, flow, tenant), ACTIONS.FETCH_LINKED_CONNECTORS, { tenant });
}

export function fetchLinkedSource(tap, catalogRequested, tenantId = undefined) {
  return apiRequest(
    () => API.getLinkedSources(tap, catalogRequested, undefined, tenantId),
    ACTIONS.FETCH_LINKED_SOURCE,
  );
}

export function doAddLinkedSource({ tenant_id, ...source }) {
  return apiRequest(() => API.addLinkedSource(source, undefined, tenant_id), ACTIONS.ADD_LINKED_SOURCE, { source });
}

export function fetchSupportedSources(flowId = getSelectedFlow()) {
  return apiRequest(() => API.getSupportedSources(flowId), ACTIONS.FETCH_SUPPORTED_SOURCES, { flowId });
}

export async function fetchAllSupportedSources() {
  const {
    flows: { supportedFlows },
  } = store.getState();

  Object.keys(supportedFlows).forEach((flowId) => store.dispatch(fetchSupportedSources(flowId)));
}

// Targets
export function fetchLinkedTargets(flowId = getSelectedFlow()) {
  return apiRequest(() => API.getLinkedTargets(undefined, flowId), ACTIONS.FETCH_LINKED_TARGETS);
}

export function fetchLinkedTargetsByFlow(flow) {
  return apiRequest(() => API.getLinkedTargets(null, flow), ACTIONS.FETCH_LINKED_TARGETS);
}

export function fetchLinkedTarget(target, catalogRequested) {
  return apiRequest(() => API.getLinkedTargets(target, undefined, undefined, catalogRequested), ACTIONS.FETCH_LINKED_TARGET);
}

export function doAddLinkedTarget(target) {
  return apiRequest(() => API.addLinkedTarget(target), ACTIONS.ADD_LINKED_TARGET, { target });
}

export function fetchSupportedTargets(flowId = getSelectedFlow()) {
  return apiRequest(() => API.getSupportedTargets(flowId), ACTIONS.FETCH_SUPPORTED_TARGETS, { flowId });
}

export async function fetchAllSupportedTargets() {
  const {
    flows: { supportedFlows },
  } = store.getState();

  Object.keys(supportedFlows).forEach((flowId) => store.dispatch(fetchSupportedTargets(flowId)));
}

// Connectors
export async function fetchAllSupportedConnectors() {
  const {
    flows: { supportedFlows },
  } = store.getState();

  Object
    .entries(supportedFlows)
    .filter(([, flowObj]) => flowObj.version === 2)
    .forEach(([flowId]) => store.dispatch(fetchSupportedConnectors(flowId)));
}

export function fetchSupportedConnectors(flowId = getSelectedFlow()) {
  return apiRequest(
    () => API.getSupportedConnectors(flowId),
    ACTIONS.FETCH_SUPPORTED_CONNECTORS,
    { flowId },
  );
}

export function fetchLinkedConnectors(catalogRequested = false, flowId = getSelectedFlow(), tenantId = getTenantId()) {
  return apiRequest(
    () => API.getLinkedConnectors(undefined, catalogRequested, flowId, tenantId),
    ACTIONS.FETCH_LINKED_CONNECTORS,
    { flowId, tenantId },
  );
}

export function fetchLinkedConnector(connectorId = undefined, catalogRequested = false, flowId = getSelectedFlow(), tenantId = getTenantId()) {
  return apiRequest(
    () => API.getLinkedConnectors(connectorId, catalogRequested, flowId, tenantId),
    ACTIONS.FETCH_LINKED_CONNECTOR,
    { flowId, tenantId },
  );
}

export function doAddLinkedConnector({ tenant_id, ...connector }) {
  return apiRequest(
    () => API.addLinkedConnector(connector, undefined, tenant_id),
    ACTIONS.ADD_LINKED_CONNECTOR,
    { connector },
  );
}

export function unlinkConnector(connectorId, flowId = getSelectedFlow(), tenantId = getTenantId()) {
  return apiRequest(
    () => API.disconnectLinkedConnector(connectorId, flowId, tenantId),
    ACTIONS.UNLINK_CONNECTOR,
    { connectorId },
  );
}

export function doUpdateLinkedSource(tap, fieldMapUpdate, tenantId = undefined) {
  return apiRequest(
    () => API.updateLinkedSource(tap, fieldMapUpdate, undefined, tenantId),
    ACTIONS.UPDATE_LINKED_SOURCE,
    {
      tap,
      fieldMap: fieldMapUpdate,
    },
  );
}

export function doUpdateLinkedSourceConfig(tap, config, tenantId = undefined) {
  return apiRequest(
    () => API.updateLinkedSourceConfig(tap, config, undefined, tenantId),
    ACTIONS.UPDATE_LINKED_SOURCE,
    {
      tap,
      config,
    },
  );
}

// Flows
export function fetchSupportedFlows() {
  return apiRequest(() => API.getSupportedFlows(), ACTIONS.FETCH_SUPPORTED_FLOWS);
}

export function fetchSupportedFlow(flow) {
  return apiRequest(() => API.getSupportedFlow(flow), ACTIONS.FETCH_SUPPORTED_FLOW, { flow });
}

export function fetchLinkedFlows(tenantId) {
  return apiRequest(() => API.getLinkedFlows(tenantId), ACTIONS.FETCH_LINKED_FLOWS);
}

export function fetchJob(flowId, tenantId = undefined, entityId = undefined, entityType = undefined) {
  return apiRequest(() => API.getJob(flowId, tenantId, entityId, entityType), ACTIONS.FETCH_LAST_JOB, { entityId });
}

export function fetchJobStatus(jobRoot, flow, userId, entityId) {
  return apiRequest(() => API.getJobStatus(jobRoot, flow, userId), ACTIONS.FETCH_LAST_JOB, { entityId });
}

export function setActiveJobType(tenant, flowId, entityId, jobType) {
  return function (dispatch) {
    dispatch({ type: ACTIONS.SET_ACTIVE_JOB_TYPE, params: { tenant, flowId, entityId, jobType } });
  };
}

export function clearActiveJobType(tenant, flowId, entityId) {
  return function (dispatch) {
    dispatch({ type: ACTIONS.CLEAR_ACTIVE_JOB_TYPE, params: { tenant, flowId, entityId } });
  };
}

export function doUpdateLinkedConnector(connectorId, fieldMapUpdate, tenantId = undefined) {
  return apiRequest(
    () => API.updateLinkedConnector(connectorId, fieldMapUpdate, undefined, tenantId),
    ACTIONS.UPDATE_LINKED_CONNECTOR,
    {
      connectorId,
      fieldMap: fieldMapUpdate,
    },
  );
}

export function doUpdateLinkedConnectorConfig(connectorId, config, tenantId = undefined) {
  return apiRequest(
    () => API.updateLinkedConnectorConfig(connectorId, config, undefined, tenantId),
    ACTIONS.UPDATE_LINKED_CONNECTOR,
    {
      connectorId,
      config,
    },
  );
}

// tenant
export function doUpdateTenantMapping(tenantConfig, entityType, entityId, tenantId) {
  return apiRequest(() => API.updateTenantMapping(tenantConfig, entityType, entityId, undefined, tenantId), ACTIONS.UPDATE_TENANT_MAPPING)
}

export function fetchTenantMapping(tenantId) {
  return apiRequest(() => API.fetchTenantConfig(undefined,tenantId), ACTIONS.FETCH_TENANT_MAPPING)
}
